import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Cpu, Stethoscope, ShieldCheck, ArrowRight, CheckCircle, MessageSquare, PhoneCall } from 'lucide-react';
import './NoidaLocation.css';
import FAQAccordion from './FAQAccordion';

const noidaSectors = [
  'Noida Sector 62 (Tech Parks & Institutions)',
  'Noida Sector 63 (IT/ITeS & Software Hubs)',
  'Noida Sector 18 & Commercial Belts',
  'Noida Special Economic Zone (NSEZ)',
  'Noida Expressway IT Corridors',
  'Greater Noida & Knowledge Park'
];

const noidaProcessDetailed = [
  { num: '01', title: 'On-Site Noida Discovery Visit', desc: 'Our senior software architects visit your office in Noida Sector 62, 63, or 18 for a face-to-face requirement mapping session.' },
  { num: '02', title: 'Architecture & Technical Scope', desc: 'Defining database architecture, cloud microservices, Figma screen designs, and mobile app UI mockups.' },
  { num: '03', title: 'Agile Development Sprints', desc: 'Iterative 2-week coding cycles with weekly staging demos and continuous feedback.' },
  { num: '04', title: 'Security Audit & QA Testing', desc: 'Automated testing, penetration audits, multi-device QA, and zero-vulnerability certification.' },
  { num: '05', title: 'Production Launch & Handover', desc: 'Production deployment on AWS/Railway, SSL installation, and complete 100% source code handover.' },
  { num: '06', title: 'On-Site Staff Training & 24/7 AMC', desc: 'Conducting staff onboarding and providing SLA-backed 24/7 technical support.' }
];

const noidaFaqsDetailed = [
  { q: 'Can your team visit our office in Noida for a face-to-face meeting?', a: 'Yes! We are located in Delhi NCR (Connaught Place, just minutes away via Metro or Expressway) and regularly conduct on-site meetings across Noida Sectors 18, 62, 63, and Expressway.' },
  { q: 'What types of software services do you offer for Noida companies?', a: 'We build custom web portals, Flutter/React Native mobile apps, manufacturing ERPs, hospital management systems (Mediway), sales CRMs, and enterprise AWS cloud migration.' },
  { q: 'Do you sign an NDA before visiting or reviewing project documents?', a: 'Absoltely. We sign strict Non-Disclosure Agreements (NDAs) before reviewing any business documents or discussing project concepts.' },
  { q: 'How long does IT software development take for a Noida business?', a: 'Standard projects take 6 to 12 weeks from discovery to live deployment depending on technical complexity.' },
  { q: 'Do we get full source code ownership upon project completion?', a: 'Yes! 100% source code, repository access, and IP rights belong entirely to your company upon launch.' },
  { q: 'Do you provide local AMC support in Noida?', a: 'Yes, we provide SLA-backed Annual Maintenance Contracts (AMC) including on-site visits and 24/7 emergency support.' },
  { q: 'What is your project payment milestone structure?', a: 'We work on transparent milestone payments: 25% kickoff, 25% prototype approval, 25% beta testing, and 25% final deployment.' }
];

export default function NoidaLocation() {
  const whatsappUrl = "https://wa.me/918287999236?text=Hi%20Wayug!%20I%20am%20located%20in%20Noida%20and%20would%20like%20to%20discuss%20a%20software%20project.";

  return (
    <section className="noida-section">
      <div className="container">
        
        {/* Hero */}
        <motion.div 
          className="noida-hero"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="noida-badge">
            <MapPin size={16} /> Noida &amp; Greater Noida
          </div>
          <h1 className="noida-title">
            Software Development Company in <span className="gradient-text">Noida</span>
          </h1>
          <p className="noida-subtitle">
            Enterprise Cloud, Custom ERP, ABDM Hospital Software &amp; AI Automation solutions for tech startups and industrial enterprises across Noida Sectors 62, 63, 18, and Greater Noida.
          </p>
          <div style={{ marginTop: '2rem', display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a 
              href={whatsappUrl} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="btn btn-primary"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
            >
              Get On-Site Noida Discovery Visit <ArrowRight size={16} />
            </a>
            <a href="/contact" className="btn btn-outline">
              Contact Delhi NCR Team
            </a>
          </div>
        </motion.div>

        {/* Focus Grid */}
        <div className="noida-grid">
          <div className="noida-card">
            <Cpu size={32} color="#60a5fa" style={{ marginBottom: '16px' }} />
            <h3>Custom Web &amp; Mobile Software Development</h3>
            <p>
              Scalable web apps, React/Node microservices, AWS/Azure cloud migration, and AI automation engineering built for Noida's high-growth tech hubs.
            </p>
          </div>

          <div className="noida-card">
            <Stethoscope size={32} color="#06b6d4" style={{ marginBottom: '16px' }} />
            <h3>Hospital Management Software (Noida Healthcare)</h3>
            <p>
              Mediway HMS for hospitals, clinics, and nursing homes across Noida Sectors 18, 62 &amp; 63. Complete ABDM certification, EMR, NABH support &amp; Tally billing.
            </p>
          </div>

          <div className="noida-card">
            <ShieldCheck size={32} color="#10b981" style={{ marginBottom: '16px' }} />
            <h3>Cybersecurity &amp; Zero-Trust Architecture</h3>
            <p>
              ISO 27001 compliance auditing, penetration testing, SOC monitoring, and DPDP Act data privacy readiness for enterprises in Noida NCR.
            </p>
          </div>
        </div>

        {/* 6-Step Delivery Process */}
        <div style={{ margin: '80px 0' }}>
          <h2 className="text-center" style={{ fontSize: '2.2rem', fontWeight: '800', marginBottom: '12px' }}>
            How Noida Projects Are Executed: <span className="gradient-text">Our 6-Step Process</span>
          </h2>
          <p className="text-center" style={{ color: '#a3a3a3', maxWidth: '700px', margin: '0 auto 40px', fontSize: '1rem', lineHeight: '1.6' }}>
            Here is how we deliver enterprise IT and custom software projects for Noida businesses.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px' }}>
            {noidaProcessDetailed.map((step) => (
              <div key={step.num} style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '16px', padding: '28px' }}>
                <span style={{ fontSize: '2rem', fontWeight: '800', color: '#60a5fa' }}>{step.num}</span>
                <h4 style={{ fontSize: '1.2rem', fontWeight: '700', margin: '12px 0 8px', color: '#fff' }}>{step.title}</h4>
                <p style={{ color: '#a3a3a3', fontSize: '0.9rem', lineHeight: '1.6', margin: 0 }}>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Noida Sectors Covered */}
        <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '20px', padding: '40px', marginBottom: '80px' }}>
          <h3 style={{ fontSize: '1.8rem', fontWeight: '800', textAlign: 'center', marginBottom: '24px' }}>
            Noida Key Hubs Covered With On-Site Visits
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
            {noidaSectors.map((s) => (
              <div key={s} style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#e5e5e5', fontSize: '0.95rem' }}>
                <CheckCircle size={16} color="#60a5fa" /> {s}
              </div>
            ))}
          </div>
        </div>

        {/* Extended FAQs */}
        <FAQAccordion 
          title="Detailed Noida IT Services FAQs" 
          faqs={noidaFaqsDetailed} 
          accentColor="#60a5fa" 
        />

        {/* Bottom High-Converting Contact Banner */}
        <div style={{ background: 'linear-gradient(135deg, rgba(37,99,235,0.15) 0%, rgba(6,182,212,0.15) 100%)', border: '1px solid rgba(37,99,235,0.3)', borderRadius: '24px', padding: '48px', textAlign: 'center' }}>
          <h2 style={{ fontSize: '2rem', fontWeight: '800', color: '#fff', marginBottom: '12px' }}>Located in Noida? Schedule an On-Site Meeting</h2>
          <p style={{ color: '#a3a3a3', maxWidth: '600px', margin: '0 auto 24px', fontSize: '1rem', lineHeight: '1.6' }}>
            Our engineering leadership is just minutes away. Schedule a free face-to-face consultation at your Noida office.
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="btn btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              <MessageSquare size={16} /> Book Noida Visit via WhatsApp
            </a>
            <a href="/contact" className="btn btn-outline" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              <PhoneCall size={16} /> Contact Team
            </a>
          </div>
        </div>

      </div>
    </section>
  );
}
